// Monthly Module - Monthly investment plan reconciliation and CSV import/export
// Extracted from index.html lines 3511-3532, 3533, 3536-3565, 3566-3596, 3600-3624, 3629-3660, 3663-3680, 3684-3709

import { S, save } from '../core/state.js';
import { mTRY, hMoTRY, hMoInvTRY, hTotTRY, hTotUSD, hVal, getMs } from '../core/math.js';
import { esc, f, fU, fT, fByCur, bc, ml, uid, now, toUSD, toUSDStrict } from '../core/utils.js';
import { toast, clMo } from '../ui/layout.js';
import { uiConfirm } from '../ui/dialog.js';
import { getActiveActions, trackActionProgress, calcDrift, generateRebalanceSuggestions } from '../modules/rebalancing.js';
import { pricePurchase } from '../core/fees.js';
import { monthLots, lotEntry, isLotSourced } from '../core/month-lots.js';
import { isPlanFunding } from '../core/capital.js';
import { lg } from './history.js';
import { renderP } from '../modules/portfolio.js';
import { renderAll } from '../app/render-loop.js';

// Globals
let editMo = null; // null=new, string=editing existing month
let _staged = {}; // Staged entries — only committed on Confirm & Save
let _moPortfolioId = null; // selected portfolio for this monthly entry

/** Get holdings scoped to the selected monthly portfolio */
function _moHoldings() {
  if (!_moPortfolioId) return S.holdings;
  return S.holdings.filter(h => h.portfolioId === _moPortfolioId);
}

/**
 * USD actually deployed in a month, from the month's OWN entries and OWN FX rates.
 *
 * Cash rows are the trap: `qty` is the amount in the holding's currency and `price` is the
 * FX rate at purchase, documented since the 2026-07-14 runtime finding. qty*price on those
 * multiplies an amount by a rate — which is why the hero deliberately prints base*mul and
 * the code says entries are "NOT summable". They are summable; they just need the Cash
 * branch. Skipped rows and unpriced rows contribute nothing.
 */
function _monthDeployedUSD(r, holdings) {
  if (!r || !r.entries) return 0;
  let sum = 0;
  (holdings || []).forEach(h => {
    const e = r.entries[h.ticker];
    if (!e || e.skipped) return;
    // A LOT-SOURCED ENTRY HAS ALREADY MOVED THE MONEY. `saveAddLot` debits `S.cash` and books
    // its own `portfolio_buy` for every lot it writes; `saveMo` books neither. Counting a
    // lot-sourced row here would put it into `buyAmt` in reconcileMo and debit the same
    // purchase a second time. Live 2026-08: sixteen such rows, USD 10,598 + TRY 91,216 already
    // booked by the lot path. (audit 2026-09-03)
    if (isLotSourced(e)) return;
    const local = h.type === 'Cash' ? (e.qty || 0) : (e.qty || 0) * (e.price || 0);
    if (!(local > 0)) return;
    const cur = h.currency || 'USD';
    if (cur === 'TRY') { if (r.usdtry > 0) sum += local / r.usdtry; }
    else if (cur === 'EUR') { if (r.eurtry > 0 && r.usdtry > 0) sum += (local * r.eurtry) / r.usdtry; }
    else sum += local;
  });
  return sum;
}

/**
 * Tickers this month already has in the book as lots, and therefore does not need saving.
 *
 * The progress counter is built from `r.entries` plus `_staged`. A row recorded through
 * `+ Lot` is in neither, so without this the month could never reach its full count and would
 * stay `partial` for ever — which is precisely why August 2026 read 13/40 while sixteen of its
 * rows were bought and paid for. (audit 2026-09-03)
 */
function _lotRecordedTickers(mo, entries) {
  const e = entries || {};
  const out = [];
  _moHoldings().forEach(h => {
    const row = e[h.ticker];
    if (row && !isLotSourced(row)) return;   // a real typed entry wins; the echo check grades it
    if (monthLots(h, mo).length) out.push(h.ticker);
  });
  return out;
}

/** Get base amount for selected portfolio */
function _moBase() {
  if (!_moPortfolioId) return S.base;
  const p = (S.portfolios || []).find(x => x.id === _moPortfolioId);
  return (p && p.base) ? p.base : S.base;
}

/** Populate portfolio select in monthly modal */
export function populateMoPortfolio() {
  const sel = document.getElementById('mPort');
  if (!sel) return;
  const ports = (S.portfolios || []).filter(p => p.status === 'active');
  if (ports.length <= 1) { sel.closest('.fgl').style.display = 'none'; return; }
  sel.closest('.fgl').style.display = '';
  sel.innerHTML = ports.map(p =>
    `<option value="${p.id}" ${p.id === _moPortfolioId ? 'selected' : ''}>${esc(p.name)}${p.isDefault ? ' (default)' : ''}</option>`
  ).join('');
}

/** Handle portfolio change in monthly modal */
export function moPortChanged() {
  const sel = document.getElementById('mPort');
  _moPortfolioId = sel ? sel.value : null;
  // Update base display
  document.getElementById('planBase').textContent = f(_moBase());
  _staged = {};
  calcPlan();
}

export function renderM(){const ms=getMs(),tots=ms.map(m=>mTRY(m)),gT=tots.reduce((a,b)=>a+b,0),gU=ms.reduce((s,m)=>{const r=S.monthlyRates[m];return s+(r?S.base*(r.mul||1):0);},0);
const reconMs=ms.filter(m=>{const r=S.monthlyRates[m];return r&&(r.status==='reconciled'||(!r.status&&!r.entries));});
document.getElementById('mSum').innerHTML=`<div class="neo-cell"><div class="l">Months</div><div class="v">${reconMs.length}</div></div>
<div class="neo-cell"><div class="l">Total TRY</div><div class="v">${fT(gT)}</div></div>
<div class="neo-cell"><div class="l">Total USD</div><div class="v">${fU(gU)}</div></div>
<div class="neo-cell"><div class="l">Monthly Base</div><div class="v">${fU(S.base)}</div></div>`;
document.getElementById('mHead').innerHTML=`<tr><th>Ticker</th><th>Type</th><th class="tr">Alloc%</th><th>Broker</th>${ms.map(m=>`<th class="tr">${ml(m)}</th>`).join('')}<th class="tr" title="Actual cost basis — recorded purchases">Total TRY</th><th class="tr">Total USD</th></tr>`;
document.getElementById('mBody').innerHTML=S.holdings.map(h=>`<tr><td><strong>${esc(h.ticker)}</strong></td><td><span class="badge ${bc(h.type)}">${esc(h.type)}</span></td>
<td class="tr">${f(h.allocationPct,1)}%</td><td>${esc(h.broker)}</td>${ms.map(m=>{
  // THE CELLS AND THE TOTAL USED TO MEASURE DIFFERENT THINGS.
  //
  // Cells were hMoTRY — the PLAN, `budget x allocationPct`, which never looks at what was
  // actually bought. The Total beside them is hTotTRY — the ACTUAL. Same row, same header,
  // nothing saying so. Live: HVZ showed TRY 0.00 in all twelve months (its allocation is 0)
  // and TRY 9,702.73 as its total; TEJ showed TRY 2,137 in April against a recorded purchase
  // of TRY 3.84. A reader comparing months against the total was comparing two different
  // quantities. (audit 2026-08-03)
  //
  // hMoInvTRY is the one that already resolves this correctly: a recorded purchase wins,
  // the plan fills in only where no purchase exists. Planned months are marked so the two
  // can still be told apart — which is the point, not a detail.
  const r = S.monthlyRates[m];
  const hasEntry = !!(r && r.entries && r.entries[h.ticker]);
  const v = hMoInvTRY(h, m);
  return `<td class="tr"${hasEntry ? '' : ' style="color:var(--t3)" title="planned, not a recorded purchase"'}>${fT(v)}${hasEntry ? '' : '<span style="font-size:9px;opacity:.6"> p</span>'}</td>`;
}).join('')}
<td class="tr"><strong>${fT(hTotTRY(h))}</strong></td><td class="tr">${fU(hTotUSD(h))}</td></tr>`).join('');
document.getElementById('mFoot').innerHTML=`<tr style="font-weight:700;border-top:2px solid var(--bd)"><td colspan="4">TOTAL</td>${ms.map((m,i)=>`<td class="tr">${fT(tots[i])}</td>`).join('')}<td class="tr">${fT(gT)}</td><td class="tr">${fU(gU)}</td></tr>`;
document.getElementById('fxBody').innerHTML=ms.map(m=>{const r=S.monthlyRates[m];
const st=r.status||'completed';const eCnt=r.entries?Object.keys(r.entries).length:0;
const stBadge=st==='reconciled'?'<span style="color:var(--grn);font-weight:600">✓ Reconciled</span>':
st==='partial'?'<span style="color:var(--yel);font-weight:600">⏳ Partial ('+eCnt+'/'+S.holdings.length+')</span>':
'<span style="color:var(--t3)">Legacy</span>';
return`<tr><td><strong>${ml(m)}</strong></td><td class="tr">${f(r.usdtry,2)}</td><td class="tr">${f(r.eurtry,2)}</td><td class="tr">${fT(mTRY(m))}</td><td class="tr">${fU(S.base*(r.mul||1))}</td><td>${stBadge}</td><td style="white-space:nowrap">${st!=='reconciled'?`<button class="btn btn-s" data-action="openAddMo" data-args='["${m}"]'>Edit</button> `:''}<button class="btn btn-s btn-d" data-action="delMo" data-args='["${m}"]'>×</button></td></tr>`;}).join('');
// Show partial month continue buttons
const partials=ms.filter(m=>{const r=S.monthlyRates[m];return r&&(r.status==='partial'||r.status==='completed')&&r.status!=='reconciled';});
const pEl=document.getElementById('partialMoBtns');
if(pEl)pEl.innerHTML=partials.map(m=>{const r=S.monthlyRates[m];const eCnt=r.entries?Object.keys(r.entries).length:0;
return`<button class="glassbtn" style="margin-right:6px" data-action="openAddMo" data-args='["${m}"]'>⏳ ${ml(m)} (${eCnt}/${S.holdings.length})</button>`;}).join('');
try{_moNeo();}catch(e){console.error('[ZAGX] _moNeo failed:',e);}}

// ===== Neon Monthly band — animated KPIs + deploy ring + months timeline (progressive disclosure) =====
function _moNeo(){
  const $=id=>document.getElementById(id);
  if(!$('moK1'))return;
  const ms=getMs();
  const recon=ms.filter(m=>{const r=S.monthlyRates[m];return r&&r.status==='reconciled';}).length;
  const gU=ms.reduce((s,m)=>{const r=S.monthlyRates[m];return s+(r?S.base*(r.mul||1):0);},0);
  $('moK1').textContent=recon;
  $('moK2').textContent=fU(gU);
  $('moK4').textContent=fU(S.base);
  const mm=$('moMeta');
  if(mm)mm.innerHTML=`<b>${ms.length}</b> months · ${fU(gU)} deployed · base ${fU(S.base)}/mo`;
  const total=S.holdings.length||1;
  // THE HERO IS ALWAYS THE CURRENT CALENDAR MONTH.
  //
  // It used to fall back to "the last month that is not reconciled" whenever this month had
  // no row, and then print that month's name under the hard-coded words THIS MONTH PLAN. On
  // 2026-08-04 the owner's Jan/Feb/Mar backfills carried no `status` at all, so the newest
  // unreconciled month was 2026-03 and the card read "MARCH 2026 · THIS MONTH PLAN" — while
  // the buy-window line two rows below it read the real clock and said the 1–4 window was
  // open. Two different months on one card, one of them labelled as the other. The Start
  // button opened March too, at March's FX, which is why August could not be edited: nothing
  // on the card pointed at August.
  //
  // A month with no plan has to read as a month with no plan. Older months left open are
  // still worth surfacing — as their own warning, below, not by impersonating this one.
  // (audit 2026-08-04)
  const key=now().slice(0,7);
  const actMonth=key, actR=S.monthlyRates[key];
  const stale=ms.filter(m=>{const x=S.monthlyRates[m];return m<key&&x&&x.status!=='reconciled'&&x.status!=='legacy';});
  let filled=0,status='none';
  if(actR){filled=actR.entries?Object.keys(actR.entries).length:0;status=actR.status||'completed';}
  const done=status==='reconciled';
  const pct=done?100:Math.round(filled/total*100);
  $('moK3').textContent=ms.length?fU(gU/ms.length):'—'; // Avg / month (mockup remap; "This month" status moved to hero)
  // ===== Hero (Heroes Mockup v0.1, approved 2026-07-14) — deployed this month + fill ring =====
  const hn=$('moHeroNum');
  if(hn){
    // Canonical month amount = base*mul (same as gU/mTRY everywhere in the app).
    // Entries' qty*price is NOT summable across holdings: Cash rows store price as the
    // TRY quote (runtime finding 2026-07-14), so per-entry conversion double-counts.
    const planUSD=actR?S.base*(actR.mul||1):S.base;
    const depUSD=actR?planUSD:0;
    $('moHeroLbl').textContent=ml(actMonth).toUpperCase()+(!actR?' · NO PLAN YET':done?' · DEPLOYED THIS MONTH':' · THIS MONTH PLAN');
    hn.textContent=fU(depUSD);
    // buy window per business rule: end-of-month 28-31 or start-of-month 1-4
    const d=new Date();const day=d.getDate();
    const win=day>=28?'open now · 28–31':day<=4?'open now · 1–4':(d.toLocaleString('en-US',{month:'short'})+' 28–31');
    let streak=0;for(let i=ms.length-1;i>=0;i--){const x=S.monthlyRates[ms[i]];if(x&&x.status==='reconciled'){streak++;}else if(streak===0){continue;}else{break;}}
    $('moHeroChips').innerHTML=
      `<span class="neo-chip">Base <b>${fU(S.base)}/mo</b></span>`+
      `<span class="neo-chip">Filled <b class="hx">${actR?(done?'✓ done':filled+'/'+total):'—'}</b></span>`+
      `<span class="neo-chip">Window <b>${win}</b></span>`+
      (streak?`<span class="neo-chip">Streak <b class="pos">${streak} reconciled</b></span>`:'')+
      (stale.length?`<span class="neo-chip" title="${stale.map(esc).join(', ')}">Open <b style="color:var(--yel)">${stale.length} earlier month${stale.length>1?'s':''}</b></span>`:'');
    const remaining=actR?S.holdings.filter(h=>!(actR.entries&&actR.entries[h.ticker])).map(h=>h.ticker):[];
    $('moHeroCtx').innerHTML=!actR
      ?`No plan for <b>${ml(key)}</b> yet — base <b>${fU(planUSD)}</b>, window <b>${win}</b>. Start it below.`
      :done
        ?`<b class="pos">${ml(actMonth)} reconciled ✓</b> · ${fU(planUSD)} plan fully deployed.`
        :remaining.length
          ?`Remaining: <b>${remaining.slice(0,3).map(esc).join(', ')}</b>${remaining.length>3?` +${remaining.length-3} more`:''} · plan ${fU(planUSD)}.`
          :`All holdings entered — reconcile to close <b>${ml(actMonth)}</b>.`;
    const ring=$('moHeroRing');
    if(ring)requestAnimationFrame(()=>requestAnimationFrame(()=>{ring.style.strokeDashoffset=(323*(1-pct/100)).toFixed(1);}));
    const rp=$('moHeroPct');if(rp)rp.textContent=actR?pct+'%':'—';
  }
  const C=2*Math.PI*34;
  $('moDeploy').innerHTML=`<div style="display:flex;gap:18px;align-items:center">
    <svg viewBox="0 0 80 80" width="86" height="86" style="flex-shrink:0">
      <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(120,150,220,.12)" stroke-width="8"/>
      <circle cx="40" cy="40" r="34" fill="none" stroke="${done?'var(--grn)':'var(--cyan)'}" stroke-width="8" stroke-linecap="round" stroke-dasharray="${(pct/100*C).toFixed(1)} ${C.toFixed(1)}" transform="rotate(-90 40 40)" style="transition:stroke-dasharray 1s cubic-bezier(.2,.7,.2,1)"/>
      <text x="40" y="46" text-anchor="middle" fill="var(--t1)" font-family="var(--mono)" font-size="15" font-weight="700">${pct}%</text>
    </svg>
    <div>
      <div style="font-family:var(--grot);font-size:15px;font-weight:600">${ml(actMonth)}</div>
      <div style="font-size:12px;color:var(--t2);margin-top:3px">${done?'Reconciled ✓':filled+' / '+total+' holdings entered'}</div>
      <button class="glassbtn" data-action="openAddMo" data-args='["${actMonth}"]' style="margin-top:10px">${done?'View':filled?'Continue':'Start'} month</button>
    </div></div>`;
  const tl=ms.slice(-12);
  $('moTimeline').innerHTML=`<div class="mo-tl">${tl.map(m=>{const x=S.monthlyRates[m];const st=x?(x.status==='reconciled'?'rec':x.status==='partial'?'part':'comp'):'none';const col=st==='rec'?'var(--grn)':st==='part'?'var(--yel)':st==='comp'?'var(--cyan)':'var(--t3)';
    return`<div class="mo-tl-i" data-action="openAddMo" data-args='["${m}"]' title="${ml(m)}"><span class="mo-tl-dot" style="background:${col};box-shadow:0 0 8px ${col}"></span><span class="mo-tl-lab">${ml(m)}</span></div>`;}).join('')||'<div class="neo-empty">No months yet</div>'}</div>`;
}

export async function delMo(m){if(!await uiConfirm('Delete '+ml(m)+' plan? This removes its entries from the breakdown.',{title:'Delete month',okText:'Delete',danger:true}))return;delete S.monthlyRates[m];save();renderM();renderP();}

export function populateMonthSelect(selId,selectedVal){
const sel=document.getElementById(selId);sel.innerHTML='';
const mNames=['January','February','March','April','May','June','July','August','September','October','November','December'];
// Range: 2026 to 2030, nearest month first
const nowDt=new Date();const curY=nowDt.getFullYear();const curM=nowDt.getMonth();
sel.innerHTML='<option value="">— Select Month —</option>';
// Build all months, sort by distance from current month
const allMo=[];
for(let y=2026;y<=2030;y++){for(let m=0;m<12;m++){allMo.push({y,m});}}
allMo.sort((a,b)=>{const da=Math.abs((a.y-curY)*12+a.m-curM),db=Math.abs((b.y-curY)*12+b.m-curM);return da-db;});
for(const{y,m}of allMo){
const val=y+'-'+String(m+1).padStart(2,'0');
const lbl=mNames[m]+' '+y;
const exists=S.monthlyRates[val];
const tag=exists?(exists.status==='partial'?' ⏳':exists.status==='reconciled'?' ✓':' ●'):'';
sel.innerHTML+=`<option value="${val}" ${val===selectedVal?'selected':''}>${lbl}${tag}</option>`;}
}

export function openAddMo(mo){
editMo=mo||null;_staged={};
populateMonthSelect('mMo',editMo||'');
// Set portfolio — use saved portfolioId if editing, else default
if(editMo&&S.monthlyRates[editMo]){
const r=S.monthlyRates[editMo];
_moPortfolioId = r.portfolioId || (S.portfolios||[]).find(p=>p.isDefault)?.id || null;
document.getElementById('mUsd').value=r.usdtry;
document.getElementById('mEur').value=r.eurtry;
document.getElementById('mMul').value=r.mul||1;
document.getElementById('mMo').disabled=true;
}else{
_moPortfolioId = (S.portfolios||[]).find(p=>p.isDefault)?.id || null;
// New month: prefill FX from the live rate so it isn't typed by hand. Only when the live rate is
// valid; user can still override. Edit path above is untouched (keeps the month's recorded rate).
const _liveU=(S.fxRate>0)?S.fxRate.toFixed(2):'';const _liveE=(S.fxRateEUR>0)?S.fxRateEUR.toFixed(2):'';
document.getElementById('mUsd').value=_liveU;document.getElementById('mEur').value=_liveE;document.getElementById('mMul').value='1';
document.getElementById('mMo').disabled=false;}
populateMoPortfolio();
document.getElementById('planBase').textContent=f(_moBase());calcPlan();document.getElementById('moM').classList.add('active');}

export function calcPlan(){const u=parseFloat(document.getElementById('mUsd').value)||0,e=parseFloat(document.getElementById('mEur').value)||0,mul=parseFloat(document.getElementById('mMul').value)||1;
const _mo=document.getElementById('mMo').value;
const entries=(editMo&&S.monthlyRates[editMo]?.entries)||{};
const base = _moBase();
const tot=base*u*mul;
const holdings = _moHoldings();
let filled=0,total=holdings.length;
// Rebalance banner above plan table — from approved actions or live drift
const _rebalBannerEl = document.getElementById('rebalBanner');
if (_rebalBannerEl) {
  let _bannerActions = getActiveActions();
  let _bannerSource = 'approved';
  // Fallback to live drift if no approved actions
  if (!_bannerActions.length) {
    try {
      const _d = calcDrift();
      if (_d) {
        _bannerActions = generateRebalanceSuggestions(_d).filter(s => s.action === 'pause' || s.action === 'boost');
        _bannerSource = 'drift';
      }
    } catch (_e) { /* optional */ }
  }
  if (_bannerActions.length) {
    const paused = _bannerActions.filter(a => a.type === 'pause' || a.action === 'pause').map(a => a.category);
    const boosted = _bannerActions.filter(a => a.type === 'boost' || a.action === 'boost').map(a => a.category);
    let bannerHtml = '<div style="padding:10px 14px;background:var(--acc-bg);border:1px solid rgba(123,63,228,.15);border-radius:8px;margin-bottom:12px;font-size:12px">';
    bannerHtml += '<div style="font-weight:600;color:var(--acc);margin-bottom:4px">' + (_bannerSource === 'drift' ? 'Rebalance drift detected — suggested adjustments:' : 'Active rebalance adjustments applied:') + '</div>';
    if (paused.length) bannerHtml += '<div style="color:var(--red)">⏸ Skip: ' + paused.join(', ') + ' — overweight, pause DCA this month</div>';
    if (boosted.length) bannerHtml += '<div style="color:var(--grn)">↑ Boost: ' + boosted.join(', ') + ' — underweight, increase DCA</div>';
    if (_bannerSource === 'drift') bannerHtml += '<div style="color:var(--t3);margin-top:4px;font-size:11px">Complete a rebalance review on Dashboard to lock in these adjustments.</div>';
    bannerHtml += '</div>';
    _rebalBannerEl.innerHTML = bannerHtml;
  } else {
    _rebalBannerEl.innerHTML = '';
  }
}
const _fund={}; // Funding by Source rollup — per-broker, per-currency (pure render, no persistence)
document.getElementById('planB').innerHTML=holdings.map((h,_i)=>{
const globalIdx = S.holdings.indexOf(h); // need global index for saveRow
// Rebalance adjustment: check if this holding's category has an active action
const _rebalCat = h.type === 'ETF' ? 'US ETF' : h.type === 'Fund' ? 'TR Fund' : h.type === 'Commodity' ? 'Commodity' : h.type === 'Cash' ? 'Cash' : (h.type === 'Stock' && h.currency === 'USD') ? 'US Stock' : (h.type === 'Stock') ? 'TR Stock' : null;
// First check approved active actions, then fallback to live drift suggestions
let _rebalAction = null;
const _approvedActions = getActiveActions();
if (_approvedActions.length && _rebalCat) {
  _rebalAction = _approvedActions.find(a => a.category === _rebalCat && a.status === 'active');
}
// CONFIRM-ALWAYS (2026-07-24): live-drift suggestions inform via the banner ONLY.
// They no longer zero/boost plan rows — only APPROVED review actions execute.
// The old fallback silently skipped rows the moment real drift crossed a band
// (surfaced when the drift scope fix made drift truthful mid-migration).
const _isPaused = _rebalAction && _rebalAction.type === 'pause';
const _isBoosted = _rebalAction && _rebalAction.type === 'boost';
const origAlloc = h.allocationPct;
// Calculate boost allocation: distribute extra budget proportionally among holdings in this category
let boostExtra = 0;
if (_isBoosted && _rebalAction && _rebalAction.monthlyExtra > 0) {
  // Find all holdings in same category to distribute proportionally
  const sameCatHoldings = holdings.filter(hh => {
    const cat2 = hh.type === 'ETF' ? 'US ETF' : hh.type === 'Fund' ? 'TR Fund' : hh.type === 'Commodity' ? 'Commodity' : hh.type === 'Cash' ? 'Cash' : (hh.type === 'Stock' && hh.currency === 'USD') ? 'US Stock' : (hh.type === 'Stock') ? 'TR Stock' : null;
    return cat2 === _rebalCat;
  });
  const catTotalAlloc = sameCatHoldings.reduce((s, hh) => s + (hh.allocationPct || 0), 0);
  const holdingShare = catTotalAlloc > 0 ? h.allocationPct / catTotalAlloc : 0;
  // Extra in USD, convert to TRY for plan
  boostExtra = _rebalAction.monthlyExtra * holdingShare * u; // monthlyExtra is USD, multiply by FX
}
const adjAlloc = _isPaused ? 0 : origAlloc;
const t = tot * (adjAlloc / 100) + boostExtra;
let l = t;
if(h.currency==='USD'&&u>0)l=t/u;if(h.currency==='EUR'&&e>0)l=t/e;const ls=h.currency==='TRY'?'₺':h.currency==='EUR'?'€':'$';
// Funding by Source: accumulate native-currency amount + USD-equivalent per broker (reflects rebalance adj since l/t already adjusted)
if(u>0&&h.broker&&l>0){const _b=h.broker;if(!_fund[_b])_fund[_b]={_usd:0};_fund[_b][h.currency]=(_fund[_b][h.currency]||0)+l;_fund[_b]._usd+=(t/u);}
const curP=S.prices[h.ticker]?S.prices[h.ticker].price:'';
// Cash-type: qty IS the currency amount (l), no division by price needed
// Stocks/ETFs/Funds: qty = local_amount / price_per_unit
const sugQty=h.type==='Cash'?(l>0?l:''):(curP&&l>0?(l/curP):'');
// Pre-fill from saved entries if editing partial month
const saved=entries[h.ticker];
// THE ROW READS THE BOOK BEFORE IT ASKS FOR THE PURCHASE.
//
// A buy entered through `+ Lot` used to render `Pending` here, because this screen only ever
// looked at `r.entries` — which `saveAddLot` does not write. Sixteen of August 2026's rows
// were in that state, and `Confirm & Save` on any of them would have added the quantity a
// second time on top of the lot. `lotEntry` is the single definition of what the lot list
// says for this month; a row it answers for is RECORDED, shows the real figures, and offers
// no door to record it again. (audit 2026-09-03)
const lotE=lotEntry(h,_mo);
const isLotRec=!!lotE&&(!saved||isLotSourced(saved));
const qVal=isLotRec?f(lotE.qty,h.type==='Cash'?2:4):(saved?saved.qty:(sugQty?f(sugQty,h.type==='Cash'?2:4):''));
// Cash: use month's FX rate as price (not current market), stocks: use current price
const cVal=isLotRec?f(lotE.price,h.type==='Cash'?4:4):(saved?saved.price:(h.type==='Cash'?(h.currency==='EUR'?e:h.currency==='USD'?u:''):(curP||'')));
const isDone=!!saved||isLotRec;
if(isDone)filled++;
// Rebalance detail labels
let _rebalLabel = '';
let _rebalDetail = '';
let _btnLabel = isDone ? 'Done' : 'Save';
let _btnBg = isDone ? 'var(--grn)' : 'var(--yel)';
let _btnColor = isDone ? '#fff' : '#000';
if (_isPaused && _rebalAction) {
  const remaining = _rebalAction.targetUSD > 0 ? fU(Math.max(_rebalAction.targetUSD - _rebalAction.progressUSD, 0)) : '';
  const monthsLeft = _rebalAction.monthsPlanned || 0;
  _rebalLabel = '<span style="font-size:9px;padding:1px 6px;border-radius:4px;background:var(--red-bg);color:var(--red);font-weight:600;margin-left:4px">SKIP</span>';
  _rebalDetail = `<div style="font-size:10px;color:var(--red);margin-top:2px">Skip this month${monthsLeft > 0 ? ' (' + monthsLeft + ' months planned)' : ''}</div>`;
  if (!isDone) { _btnLabel = 'Acknowledge Skip'; _btnBg = 'var(--red-bg)'; _btnColor = 'var(--red)'; }
} else if (_isBoosted && _rebalAction) {
  const extra = _rebalAction.monthlyExtra || 0;
  const remaining = _rebalAction.targetUSD > 0 ? Math.max(_rebalAction.targetUSD - _rebalAction.progressUSD, 0) : 0;
  const holdingExtraUSD = boostExtra > 0 && u > 0 ? boostExtra / u : 0;
  _rebalLabel = '<span style="font-size:9px;padding:1px 6px;border-radius:4px;background:var(--grn-bg);color:var(--grn);font-weight:600;margin-left:4px">BOOST +' + fU(holdingExtraUSD) + '</span>';
  _rebalDetail = remaining > 0 ? `<div style="font-size:10px;color:var(--grn);margin-top:2px">Category: ${fU(remaining)} remaining across ${_rebalCat}</div>` : `<div style="font-size:10px;color:var(--grn);margin-top:2px">Target reached!</div>`;
}
const statusHtml = isLotRec ? '<span style="color:var(--grn);font-size:11px;font-weight:600" title="'+lotE.lotIds.length+' lot(s) already recorded in this month — nothing to save">✓ Recorded</span>' : isDone ? '<span style="color:var(--grn);font-size:11px;font-weight:600">✓ Done</span>' : _isPaused ? '<span style="color:var(--red);font-size:11px">Skip</span>' : '<span style="color:var(--t3);font-size:11px">Pending</span>';
return`<tr style="${isDone?'opacity:.7':''}${_isPaused && !isDone?' background:var(--red-bg);':''}"><td><strong>${esc(h.ticker)}</strong>${_rebalLabel}${_rebalDetail}</td><td><span class="badge ${bc(h.type)}">${esc(h.type)}</span></td>
<td style="font-size:11px;color:var(--yel)">${h.buyWindow||'—'}</td>
<td class="tr">${_isPaused ? '<s style="color:var(--t3)">' + f(origAlloc,1) + '%</s> 0%' : (_isBoosted && boostExtra > 0 ? f(origAlloc,1) + '% <span style="color:var(--grn);font-size:10px">+' + f(boostExtra / tot * 100, 1) + '%</span>' : f(adjAlloc,1) + '%')}</td><td class="tr">${u ? (_isBoosted && boostExtra > 0 ? '<span style="color:var(--t3);text-decoration:line-through;font-size:10px">' + fT(tot * origAlloc / 100) + '</span> ' + fT(t) : fT(t)) : '—'}</td><td class="tr">${u ? (_isBoosted && boostExtra > 0 ? ls + f(l) + ' <span style="color:var(--grn);font-size:10px">(+' + ls + f(boostExtra > 0 && u > 0 ? (h.currency === 'TRY' ? boostExtra : boostExtra / (h.currency === 'EUR' ? e : u)) : 0) + ')</span>' : ls + f(l)) : '—'}</td>
<td class="tr">${isLotRec ? '<span style="font-size:11px;color:var(--grn)">'+qVal+'</span>' : _isPaused && !isDone ? '<span style="font-size:10px;color:var(--t3)">—</span>' : '<input class="price-input" style="width:80px" id="mq_'+globalIdx+'" step="any" value="'+qVal+'" placeholder="qty">'}</td>
<td class="tr">${isLotRec ? '<span style="font-size:11px;color:var(--grn)">'+cVal+'</span>' : _isPaused && !isDone ? '<span style="font-size:10px;color:var(--t3)">—</span>' : '<input class="price-input" style="width:90px" id="mc_'+globalIdx+'" step="any" value="'+cVal+'" placeholder="'+h.currency+'">'}</td>
<td class="tr" style="color:var(--t3)">${h.qty?f(h.qty,4):'0'}</td>
<td class="tr" style="color:var(--t3)">${h.type==='Cash'?(h.avgCost?'FX '+f(h.avgCost):'—'):(h.avgCost?fByCur(h.currency,h.avgCost):'—')}</td>
<td class="tr">${statusHtml}</td><td style="white-space:nowrap">${isLotRec ? `<span style="font-size:10px;color:var(--t3)">from ${lotE.lotIds.length} lot${lotE.lotIds.length===1?'':'s'}</span>` : `<button id="rbtn_${globalIdx}" class="btn btn-s" style="padding:3px 12px;font-size:11px;background:${_btnBg};color:${_btnColor};border:none;border-radius:6px;cursor:pointer;font-weight:600" data-action="saveRow" data-args='[${globalIdx}]'>${_btnLabel}</button>${(_isPaused || isDone || (h.allocationPct || 0) <= 0) ? '' : `<button id="sbtn_${globalIdx}" class="btn btn-s" title="Nothing was bought in this line this month" style="padding:3px 8px;font-size:10px;margin-left:4px;background:transparent;color:var(--t3);border:1px solid var(--t3);border-radius:6px;cursor:pointer" data-action="skipRow" data-args='[${globalIdx}]'>Not bought</button>`}`}</td></tr>`;}).join('');
// Funding by Source band — per-broker transfer totals (pure render from _fund, no persistence/no save)
const _fbsEl=document.getElementById('fundBySource');
if(_fbsEl){const _brokers=Object.keys(_fund);
if(u>0&&_brokers.length){const _sym={TRY:'₺',EUR:'€',USD:'$'};
const _parts=_brokers.sort((a,b)=>_fund[b]._usd-_fund[a]._usd).map(b=>{const o=_fund[b];
const _cur=Object.keys(o).filter(k=>k!=='_usd'&&o[k]>0.005).sort((x,y)=>o[y]-o[x]).map(k=>_sym[k]+f(o[k],(k==='USD'||k==='EUR')?2:0)).join(' + ');
return `<span style="white-space:nowrap"><strong style="color:var(--acc)">${esc(b)}</strong> ${_cur} <span style="color:var(--t3)">(≈${fU(o._usd)})</span></span>`;}).join('<span style="color:var(--t3);margin:0 10px">·</span>');
_fbsEl.innerHTML=`<div class="gls-soft" style="padding:10px 14px;border:1px solid var(--bd);border-radius:8px;margin-bottom:12px;font-size:13px;display:flex;flex-wrap:wrap;align-items:center;gap:4px"><span style="font-weight:600;color:var(--t2);margin-right:8px">💰 Fund this month:</span>${_parts}</div>`;
}else{_fbsEl.innerHTML='';}}
document.getElementById('planF').innerHTML=`<tr style="font-weight:700;border-top:2px solid var(--bd)"><td colspan="4">TOTAL</td><td class="tr">${u?fT(tot):'—'}</td><td class="tr">${u?fU(base*mul):'—'}</td><td colspan="6"></td></tr>`;
// Progress bar
const pct=total>0?Math.round(filled/total*100):0;
document.getElementById('planProg').style.width=pct+'%';
document.getElementById('planProgTxt').textContent=filled+'/'+total+' ('+pct+'%)';
// Reconciliation button color
const btn=document.getElementById('reconBtn');
if(btn){if(filled>=total){btn.style.background='var(--grn)';btn.style.color='#fff';btn.textContent='Monthly Reconciliation ✓';}
else{btn.style.background='var(--yel)';btn.style.color='#000';btn.textContent='Monthly Reconciliation ('+filled+'/'+total+')';}}}

/**
 * "Not bought" — record that this line bought nothing this month, and mean it.
 *
 * A month closes only when every row has an entry, and stageMoRow refuses a row with no
 * quantity. That is right for a DCA month, where every line does get bought. It is wrong for
 * a month like 2026-08, funded by SALES rather than by the monthly base: most lines genuinely
 * bought nothing, and the owner was left with two bad options — type a purchase that never
 * happened, or leave the month permanently open.
 *
 * The third option already existed and had no door. `saveMo` has always understood a staged
 * row with `skipped:true`: it writes qty 0 / price 0, records WHY, counts toward completion,
 * and — the part that matters — leaves the position and the price cache untouched. Until now
 * the only ways in were a 0% target or an approved CATEGORY-level rebalance pause, neither of
 * which can say "this line, this month, nothing".
 *
 * The reason is its own value. `zero-target` means the policy says never; `rebalance` means an
 * approved review paused the category; `not-bought` means the target is live and the purchase
 * simply did not happen. Collapsing them would lose the only thing the row still has to say.
 * (audit 2026-08-07)
 */
export function skipRow(i){
  const mo=document.getElementById('mMo').value;
  if(!mo)return toast('Set month & FX rates first','a-r');
  const h=S.holdings[i];if(!h)return;
  if(monthLots(h,mo).length)return toast(h.ticker+': already recorded as a lot in '+ml(mo)+' — nothing to mark','a-r');
  _staged[h.ticker]={idx:i,qty:0,price:0,skipped:true,skipReason:'not-bought'};
  const btn=document.getElementById('rbtn_'+i);
  if(btn){btn.textContent='Not bought';btn.style.background='var(--t3)';btn.style.color='#fff';}
  const sb=document.getElementById('sbtn_'+i); if(sb)sb.style.display='none';
  const q=document.getElementById('mq_'+i), c=document.getElementById('mc_'+i);
  if(q){q.value='';q.disabled=true;} if(c){c.value='';c.disabled=true;}
  btn?.closest('tr')?.setAttribute('style','opacity:.5');
  const existing=(S.monthlyRates[mo]?.entries)||{};
  const totalDone=new Set([...Object.keys(existing),...Object.keys(_staged),..._lotRecordedTickers(mo,existing)]).size;
  const total=_moHoldings().length;
  const pct=total>0?Math.round(totalDone/total*100):0;
  document.getElementById('planProg').style.width=pct+'%';
  document.getElementById('planProgTxt').textContent=totalDone+'/'+total+' ('+pct+'%)';
  lg('portfolio',h.ticker,'Month '+ml(mo)+': marked not bought');
  toast(h.ticker+': recorded as not bought this month','a-g');
}

export function saveRow(i){
const mo=document.getElementById('mMo').value;
const u=parseFloat(document.getElementById('mUsd').value)||0;
const e=parseFloat(document.getElementById('mEur').value)||0;
if(!mo||!u||!e)return toast('Set month & FX rates first','a-r');
const h=S.holdings[i];if(!h)return;
// THE BOOK ALREADY HAS THIS PURCHASE. Saving it here would add the quantity a second time,
// because `saveMo` blends into `h.qty` and knows nothing about `h.lots`. (audit 2026-09-03)
if(monthLots(h,mo).length)return toast(h.ticker+': already recorded as a lot in '+ml(mo)+' — saving would double it','a-r');
// Check if this holding is paused by rebalancing — allow skip without qty/price
const _skipCat = h.type === 'ETF' ? 'US ETF' : h.type === 'Fund' ? 'TR Fund' : h.type === 'Commodity' ? 'Commodity' : h.type === 'Cash' ? 'Cash' : (h.type === 'Stock' && h.currency === 'USD') ? 'US Stock' : (h.type === 'Stock') ? 'TR Stock' : null;
let _isSkipped = false;
let _skipWhy = 'rebalance';
// A HOLDING WITH A 0% TARGET IS NOT BOUGHT THIS MONTH. That is what 0% means.
//
// It still demanded a quantity and refused zero, so the eleven positions Option A cut to
// zero could never be completed and the month could never reach 40/40 to reconcile. The
// only skip path that existed required an APPROVED rebalance review action, at CATEGORY
// level — which is both unavailable until a review is confirmed and far too coarse: pausing
// "TR Fund" would also skip NVB and TEJ, and NVB is the single largest buy of the migration
// month. A target of zero is a per-holding fact and needs its own path. (audit 2026-08-04)
if ((h.allocationPct || 0) <= 0) { _isSkipped = true; _skipWhy = 'zero-target'; }
if (!_isSkipped) {
  try {
    // CONFIRM-ALWAYS (2026-07-24): only APPROVED review actions can mark a row skipped.
    // The live-drift fallback here mirrored the calcPlan one and is removed with it.
    const _skipActions = getActiveActions();
    if (_skipActions.find(a => a.category === _skipCat && a.type === 'pause')) _isSkipped = true;
  } catch (_e) { /* optional */ }
}
if (_isSkipped) {
  // Stage as skipped — qty 0, price 0
  _staged[h.ticker] = { idx: i, qty: 0, price: 0, skipped: true, skipReason: _skipWhy };
  const btn = document.getElementById('rbtn_' + i);
  if (btn) { btn.textContent = 'Skipped'; btn.style.background = 'var(--t3)'; btn.style.color = '#fff'; }
  btn?.closest('tr')?.setAttribute('style', 'opacity:.5');
  toast(h.ticker + (_skipWhy === 'zero-target' ? ': skipped (target 0%)' : ': skipped (rebalance)'), 'a-g');
  // Update progress
  const existing = (document.getElementById('mMo').value && S.monthlyRates[mo]?.entries) || {};
  const totalDone = new Set([...Object.keys(existing), ...Object.keys(_staged), ..._lotRecordedTickers(mo, existing)]).size;
  const total = _moHoldings().length;
  const pct = total > 0 ? Math.round(totalDone / total * 100) : 0;
  document.getElementById('planProg').style.width = pct + '%';
  document.getElementById('planProgTxt').textContent = totalDone + '/' + total + ' (' + pct + '%)';
  return;
}
const newQty=parseFloat(document.getElementById('mq_'+i)?.value)||0;
const newCost=parseFloat(document.getElementById('mc_'+i)?.value)||0;
if(!newQty||!newCost)return toast(h.ticker+': qty & price required','a-r');
// Stage this entry.
// The number typed in is the QUOTED price — what the broker screen showed. What the position
// cost is that plus the commission, which was never recorded and left all thirteen IBKR
// positions understating cost by $39 in total, growing every month. `price` becomes the net
// per-unit cost so every existing sum stays correct without learning a new shape; the quote
// and the charge are kept beside it for the report. An unverified fee model returns the quote
// untouched and says so — it does not become a silent zero. (audit 2026-08-03)
const _priced=pricePurchase(h,newQty,newCost);
_staged[h.ticker]={idx:i,qty:_priced.qty,price:_priced.price,grossPrice:_priced.grossPrice,feeLocal:_priced.feeLocal,feeModel:_priced.feeModel};
// Update rebalance action progress if this holding has a boost action
try {
  const _cat = h.type === 'ETF' ? 'US ETF' : h.type === 'Fund' ? 'TR Fund' : h.type === 'Commodity' ? 'Commodity' : h.type === 'Cash' ? 'Cash' : (h.type === 'Stock' && h.currency === 'USD') ? 'US Stock' : (h.type === 'Stock') ? 'TR Stock' : null;
  if (_cat) {
    const actions = getActiveActions();
    const action = actions.find(a => a.category === _cat && a.type === 'boost' && a.status === 'active');
    if (action) {
      // Rebalance progress is measured in USD. Without a rate the purchase has no USD value,
      // and `|| 1` credited the TRY figure as dollars — inflating progress toward a target by
      // roughly the exchange rate. Skip the tracking rather than record a fabricated amount.
      const purchaseUSD = h.currency === 'USD' ? newQty * newCost : toUSDStrict(newQty * newCost, h.currency);
      if (purchaseUSD != null) trackActionProgress(action.id, purchaseUSD);
      else console.warn('[ZAGX] rebalance progress not tracked for ' + h.ticker + ' — no usable FX rate');
    }
  }
} catch (_e) { /* rebalance tracking optional */ }
// Visual: button → green Done
const btn=document.getElementById('rbtn_'+i);
if(btn){btn.textContent='Done';btn.style.background='var(--grn)';btn.style.color='#fff';}
// Visual: row opacity
btn?.closest('tr')?.setAttribute('style','opacity:.7');
// Update progress counter
const existing=(document.getElementById('mMo').value&&S.monthlyRates[mo]?.entries)||{};
const totalDone=new Set([...Object.keys(existing),...Object.keys(_staged),..._lotRecordedTickers(mo,existing)]).size;
const total=_moHoldings().length;
const pct=total>0?Math.round(totalDone/total*100):0;
document.getElementById('planProg').style.width=pct+'%';
document.getElementById('planProgTxt').textContent=totalDone+'/'+total+' ('+pct+'%)';
// Update reconciliation button
const reconBtn=document.getElementById('reconBtn');
if(reconBtn){if(totalDone>=total){reconBtn.style.background='var(--grn)';reconBtn.style.color='#fff';reconBtn.textContent='Monthly Reconciliation ✓';}
else{reconBtn.style.background='var(--yel)';reconBtn.style.color='#000';reconBtn.textContent='Monthly Reconciliation ('+totalDone+'/'+total+')';}}
toast(h.ticker+' ✓ staged ('+totalDone+'/'+total+')','a-b');}

export function saveMo(){const mo=document.getElementById('mMo').value,u=parseFloat(document.getElementById('mUsd').value),e=parseFloat(document.getElementById('mEur').value),mul=parseFloat(document.getElementById('mMul').value)||1;
if(!mo||!u||!e)return toast('All fields required','a-r');
// A month can be complete with nothing staged: every row bought through `+ Lot` is already in
// the book and only needs its echo written into the month. Refusing here left such a month
// permanently `partial`. (audit 2026-09-03)
const _pendingLots=_lotRecordedTickers(mo,(S.monthlyRates[mo]||{}).entries||{})
  .filter(t=>!((S.monthlyRates[mo]||{}).entries||{})[t]);
if(!Object.keys(_staged).length&&!_pendingLots.length)return toast('No staged entries — click Save on each row first','a-r');
// ── TW BEFORE snapshot ──
let _twBefore = 0;
{
  let gV = 0; S.holdings.forEach(h => { const v = hVal(h); if (v.vU) gV += v.vU; });
  // LEDGER basis, to match computeTotalWealthUSD — this is a wealth delta, not liquidity.
  const cUSD = (S.cash?.USD || 0) + toUSD(S.cash?.TRY || 0, 'TRY') + toUSD(S.cash?.EUR || 0, 'EUR');  // ledger-basis: capital identity, deliberate
  let tInv = 0, tPnL = 0;
  S.trades.filter(t => t.status !== 'Closed').forEach(t => { const p = S.prices[t.ticker]; if (!p) return; tInv += toUSD(t.entry * t.qty, t.currency || 'USD'); tPnL += toUSD((t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage, t.currency || 'USD'); });
  _twBefore = gV + cUSD + tInv + tPnL;
}
// Init or update monthlyRates
if(!S.monthlyRates[mo])S.monthlyRates[mo]={usdtry:u,eurtry:e,mul,status:'partial',entries:{},portfolioId:_moPortfolioId||null};
else{S.monthlyRates[mo].usdtry=u;S.monthlyRates[mo].eurtry=e;S.monthlyRates[mo].mul=mul;}
const r=S.monthlyRates[mo];if(!r.entries)r.entries={};
// Process all staged entries — update holdings qty & avgCost
let newEntries=0;
// ── THE GUARD. A staged row whose purchase is already a lot is DROPPED, not written. ────────
// `saveMo` blends into `h.qty` and never looks at `h.lots`; `saveAddLot` writes the lot, moves
// the cash and never looks at `r.entries`. Writing both is the same purchase counted twice.
// The screen no longer offers the button, but staging survives a re-render and an old cached
// build can still reach here, so the refusal lives in the writer, not only in the view.
// It is loud: every dropped ticker is named in a toast and in the log. (audit 2026-09-03)
const _dropped=[];
Object.keys(_staged).forEach(t=>{
  const h=S.holdings[_staged[t].idx];
  if(h&&monthLots(h,mo).length){_dropped.push(t);delete _staged[t];}
});
if(_dropped.length){
  lg('system','—','Month '+ml(mo)+': dropped '+_dropped.length+' staged row(s) already recorded as lots — '+_dropped.join(', '));
  toast(_dropped.join(', ')+': already in the book as lots, not written again','a-w');
}
// ── THE ECHO. What the lots say, written into the month so it can complete. ──────────────────
// Position, average cost, price cache and cash are all deliberately untouched: the lot already
// did every one of those. This entry exists so the month can reach its count, so the matrix
// stops reading zero for a purchase that happened, and so `plan.lotEcho` has something to
// grade. Running it twice writes the same object twice.
_lotRecordedTickers(mo,r.entries).forEach(t=>{
  const h=_moHoldings().find(x=>x.ticker===t);
  const le=h&&lotEntry(h,mo);
  if(!le)return;
  const prev=r.entries[t];
  if(prev&&prev.qty===le.qty&&prev.price===le.price)return;
  r.entries[t]={qty:le.qty,price:le.price,date:now(),source:'lots',lotIds:le.lotIds};
  newEntries++;
  lg('portfolio',t,'Month '+ml(mo)+': recorded from '+le.lotIds.length+' lot(s) — '+f(le.qty,4)+' @ '+fByCur(h.currency,le.price)+' (no position change)');
});
Object.entries(_staged).forEach(([ticker,st])=>{
const h=S.holdings[st.idx];if(!h)return;
// A SKIPPED ROW BOUGHT NOTHING. It must not touch the position, and above all it must not
// touch the price cache: the line below writes S.prices[ticker] unconditionally, so a
// skipped row used to publish a MARKET PRICE OF ZERO. With skips previously reachable only
// through an approved category pause that was rare; with 0%-target skips it would have
// fired on eleven tickers at once and taken their value out of net worth. The entry is
// still written, so the row counts toward completion and the month can reconcile.
// (audit 2026-08-04)
if (st.skipped) {
  r.entries[ticker] = { qty: 0, price: 0, date: now(), skipped: true,
    ...(st.skipReason ? { skipReason: st.skipReason } : {}) };
  newEntries++;
  return;
}
const prev=r.entries[ticker];
if(!prev){
const oldQty=h.qty||0;const oldTotalCost=(h.avgCost||0)*oldQty;const newTotalCost=st.qty*st.price;
h.qty=oldQty+st.qty;h.avgCost=h.qty>0?(oldTotalCost+newTotalCost)/h.qty:st.price;
lg('portfolio',ticker,'Month '+ml(mo)+': +'+f(st.qty,4)+' @ '+fByCur(h.currency,st.price)+' | total:'+f(h.qty,4)+' avg:'+fByCur(h.currency,h.avgCost));
}else if(prev.qty!==st.qty||prev.price!==st.price){
const oldQtyH=h.qty||0;const oldAvgH=h.avgCost||0;
const revertQty=oldQtyH-prev.qty;const revertCost=revertQty>0?(oldAvgH*oldQtyH-prev.qty*prev.price)/revertQty:0;
h.qty=revertQty+st.qty;h.avgCost=h.qty>0?(revertCost*revertQty+st.qty*st.price)/h.qty:st.price;
lg('portfolio',ticker,'Month '+ml(mo)+': updated to '+f(st.qty,4)+' @ '+fByCur(h.currency,st.price));}
// The price cache is a MARKET quote, so it takes the quoted number, not the net cost.
// Feeding it the fee-inclusive price would value the whole position at cost-plus-commission.
S.prices[ticker]={price:st.grossPrice!=null?st.grossPrice:st.price,changePct:0,src:'manual',ts:Date.now(),cur:h.currency};
r.entries[ticker]={qty:st.qty,price:st.price,date:now(),
  ...(st.grossPrice!=null?{grossPrice:st.grossPrice}:{}),
  ...(st.feeLocal!=null?{feeLocal:st.feeLocal}:{}),
  ...(st.feeModel?{feeModel:st.feeModel}:{})};
newEntries++;});
// Update status
const filled=Object.keys(r.entries).length;
const moHoldingsCount = _moHoldings().length;
r.status=filled>=moHoldingsCount?'completed':'partial';
_staged={};// clear staging
lg('system','—','Month '+ml(mo)+': committed '+newEntries+' entries ('+filled+'/'+moHoldingsCount+' total)');
save();
// ── TW AFTER snapshot + delta log ──
{
  let gV = 0; S.holdings.forEach(h => { const v = hVal(h); if (v.vU) gV += v.vU; });
  // LEDGER basis, to match computeTotalWealthUSD — this is a wealth delta, not liquidity.
  const cUSD = (S.cash?.USD || 0) + toUSD(S.cash?.TRY || 0, 'TRY') + toUSD(S.cash?.EUR || 0, 'EUR');  // ledger-basis: capital identity, deliberate
  let tInv = 0, tPnL = 0;
  S.trades.filter(t => t.status !== 'Closed').forEach(t => { const p = S.prices[t.ticker]; if (!p) return; tInv += toUSD(t.entry * t.qty, t.currency || 'USD'); tPnL += toUSD((t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage, t.currency || 'USD'); });
  const _twAfter = gV + cUSD + tInv + tPnL;
  const delta = _twAfter - _twBefore;
  console.log(`[ZAGX] saveMo: TW before: $${_twBefore.toFixed(2)} → TW after: $${_twAfter.toFixed(2)} (${delta >= 0 ? '+' : ''}$${delta.toFixed(2)})`);
  console.log(`[ZAGX] saveMo breakdown: gVal=$${gV.toFixed(2)} cash=$${cUSD.toFixed(2)} tradeInv=$${tInv.toFixed(2)} tradePnL=$${tPnL.toFixed(2)}`);
  lg('system','—',`saveMo TW: $${_twBefore.toFixed(2)} → $${_twAfter.toFixed(2)} (${delta >= 0 ? '+' : ''}$${delta.toFixed(2)})`);
}
// Don't close modal — recalc plan in place so user can continue adding
calcPlan();
toast(ml(mo)+': '+newEntries+' entries committed ('+filled+'/'+moHoldingsCount+')','a-b');}

export function reconcileMo(){const mo=document.getElementById('mMo').value;
if(!mo||!S.monthlyRates[mo])return toast('Save entries first','a-r');
const r=S.monthlyRates[mo];
if(r.status==='reconciled')return toast('Already reconciled','a-r');
// Set portfolio context from saved rate
_moPortfolioId = r.portfolioId || null;
const moH = _moHoldings();
if(!moH.length)return toast('No holdings defined — add holdings first','a-r');
const filled=Object.keys(r.entries||{}).length;
if(filled<moH.length)return toast('Not complete yet: '+filled+'/'+moH.length+' — fill remaining holdings first','a-r');
// Finalize the month in the ledger.
//
// THE DEPOSIT AND THE DEPLOYMENT ARE TWO DIFFERENT NUMBERS, and until 2026-08-04 this
// booked one figure twice: deposit +base*mul and portfolio_buy -base*mul, a net-zero pair.
// That is right for an ordinary month, where the only money buying anything is the month's
// contribution. It is wrong for a month that also redeploys capital already inside the
// sleeve.
//
// August 2026 is that month: thirteen positions are sold and twelve bought, ~$8.4k of it a
// re-domiciling of existing assets rather than new money. execExitH credits the sale
// proceeds to cash; the monthly plan is where the buy side is recorded; and with the buy leg
// pinned to base*mul the ledger would have carried ~$8,400 of cash that was spent but never
// shown leaving. The capital reconciliation band is ±2% of assets at cost — roughly $1,400 —
// so the residual would have gone red for a book that was in fact correct.
//
// deposit  = base * mul     — new external money, the Total Wealth P&L denominator
// buy      = actually deployed — every entry, at the month's own FX rates
// The two are equal in a normal month and differ by exactly the redeployment otherwise.
// (audit 2026-08-04)
if(r.status!=='reconciled'){
const depositAmt=_moBase()*(r.mul||1);
const deployedAmt=_monthDeployedUSD(r,moH);
// NEITHER LEG MAY BOOK MONEY THAT IS ALREADY IN THE LEDGER.
//
// The deposit was written blind. August 2026 already carries `deposit USD 2,400 — 2026-08`,
// entered on the 28th through the transfer path, so pressing this button would have wired the
// month's contribution a second time and credited $2,400 of cash that never arrived. Both note
// formats are matched: the v5 migration wrote the raw month key, this function writes ml().
//
// The buy leg had the mirror defect in its fallback. `deployedAmt > 0 ? deployedAmt :
// depositAmt` was written for LEGACY months, which have no entries at all and so nothing to
// measure. On a month recorded entirely through `+ Lot` — where every purchase already has its
// own portfolio_buy and its own cash debit — that fallback invents a buy for money that was
// spent weeks ago.
//
// THE DISCRIMINATOR IS THE LOT, NOT THE SKIP. A month of all-skipped rows deployed nothing
// anywhere, and the fallback is right about it: the contribution arrived and is sitting as
// cash, so booking +D and -D nets to zero and the 2026-08-04 contract stands. A month with
// lot-sourced rows is the different case — its buys are ALREADY in the ledger with their own
// cash debits, and a fallback would book them again. (audit 2026-09-03)
// BOTH LEGS, OR NEITHER. Booking only the deposit against its own history left the buy leg
// free to double, and the three months that predate the status field — 2026-01, -02, -03 —
// are exactly that shape: entries typed by hand, so `deployedAmt > 0`, and a
// `Portfolio allocation — 2026-01` row already sitting in the ledger from the v5 migration.
// Closing one of them would have subtracted the same $2,400 twice. Two note formats again:
// the migration wrote the raw month key, this function writes ml(). (audit 2026-09-03)
const _txNote=(t)=>String((t&&t.note)||'');
const _bookedUSD=(type,prefix)=>(S.cashTxs||[]).some(t=>t&&t.type===type&&(t.currency||'USD')==='USD'
  &&(_txNote(t).includes(prefix+ml(mo))||_txNote(t).includes(prefix+mo)));
// A MONTH'S FUNDING HAS TWO SHAPES AND THIS LEG COULD ONLY SEE ONE.
//
// `_depBooked` asks "did I already declare this month's contribution?" and looks for a prior
// `deposit` row. But dollars also arrive as a CONVERSION — which is how they actually arrived in
// September 2026: TRY 431,903.76 -> USD 8,884.00, recorded as `fx_convert`, no declaration.
//
// That was harmless while the buy leg fired too: +2,400 and -2,400 netted to zero and moved no
// balance, which is the behaviour the owner remembers. Two changes ended it — 2026-08-04 made
// the buy leg the actual deployment, and `16cf8fb` stopped booking it at all for a month
// recorded entirely through `+ Lot`. September is that month: 25 of its 39 entries are
// lot-sourced. So the deposit leg fired ALONE and credited USD 2,400 that never arrived, as a
// `deposit` row, which classifyCashTx reads as EXTERNAL_IN — inflating contributed capital and
// with it Total Wealth P&L and XIRR.
//
// `16cf8fb` closed the buy leg's half of this and left the deposit leg's half open, and the gap
// was answered by telling the owner not to press the button. A rule the owner has to remember is
// a rule that has not been written down. `integrity.js:810` already says it in prose -- "if the
// month already carries an fx_convert, do not reconcile it" -- and that is a remedy addressed to
// a human. This is the same rule, in the function.
//
// BINARY, not amount-matched, deliberately: the conversion is the record of what arrived, and
// crediting the difference would invent a number nobody wired. `capital.declaredVsWired` exists
// to report a shortfall and still does. (audit 2026-09-11)
const _convertedUSD=(S.cashTxs||[]).some(t=>t&&t.type==='fx_convert'&&(t.currency||'USD')==='USD'
  &&(Number(t.amount)||0)>0&&String(t.date||'').slice(0,7)===mo&&isPlanFunding(t));
const _depBooked=_bookedUSD('deposit','Monthly IBKR deposit — ')||_convertedUSD;
const _buyBooked=_bookedUSD('portfolio_buy','Portfolio allocation — ');
const _hasLotEntries=Object.values(r.entries||{}).some(e=>e&&e.source==='lots');
const depositAdd=_depBooked?0:depositAmt;
const buyAmt=deployedAmt>0?deployedAmt:(_hasLotEntries?0:depositAmt);
const buyAdd=_buyBooked?0:buyAmt;
// 1. External deposit — tracked for net external capital (Total Wealth P&L)
if(depositAdd>0)S.cashTxs.unshift({id:uid(),date:now(),type:'deposit',amount:depositAdd,currency:'USD',note:'Monthly IBKR deposit — '+ml(mo)});
else lg('system','—','Month '+ml(mo)+': deposit leg skipped — '+(_convertedUSD?'funding already recorded as an IBKR conversion':'already in the ledger'));
// 2. Portfolio allocation — what actually went into positions
if(buyAdd>0)S.cashTxs.unshift({id:uid(),date:now(),type:'portfolio_buy',amount:-buyAdd,currency:'USD',
  note:'Portfolio allocation — '+ml(mo)+(Math.abs(buyAdd-depositAmt)>1?' (incl. '+fU(buyAdd-depositAmt)+' redeployed)':'')});
else lg('system','—','Month '+ml(mo)+': buy leg not booked — '+(_buyBooked?'already in the ledger':'every row was recorded as a lot or bought nothing'));
// 3. THE BALANCE. This function is the only writer in the app that appends to `cashTxs`
// without moving `S.cash`, and it got away with it for seven months because the two rows
// above were the SAME NUMBER: +base*mul and -base*mul net to zero, so the invariant
// `S.cash[cur] === sum of non-audit cashTxs[cur]` survived by arithmetic accident.
//
// The 2026-08-04 change made the buy leg the ACTUAL deployment. The pair no longer nets, and
// USD has no self-healing recompute — `backfillSalaryCashTxs` only rebuilds currencies that
// carry audit rows, and every audit row in this book is TRY salary. So the first month whose
// entries are genuinely filled would have left the USD balance and its own ledger disagreeing
// by exactly (buyAmt - depositAmt), permanently, with nothing to notice.
//
// The adjustment is also the real-world truth, not a patch: wire in depositAmt, spend buyAmt,
// and the brokerage cash balance moves by the difference. Deploy more than was wired and it
// came out of cash already sitting there.
// Measured 2026-08-14: drift was exactly 0.00 in all three currencies, i.e. the invariant
// held on live data — this keeps it holding. Graded from now on by `cash.ledgerMatch` (B3).
// (MOVE-001)
S.cash.USD=(S.cash.USD||0)+depositAdd-buyAdd;
r.status='reconciled';
lg('system','—','Month '+ml(mo)+' RECONCILED — '+filled+' holdings, deposit: '+fU(depositAdd)
  +(Math.abs(buyAdd-depositAmt)>1?', deployed: '+fU(buyAdd):''));
save();clMo('moM');renderAll();
toast(depositAdd||buyAdd
  ? ml(mo)+' reconciled ✓ — booked '+fU(depositAdd)+' in, '+fU(buyAdd)+' out'
  : ml(mo)+' closed ✓ — both legs were already in the ledger, nothing booked','a-b');
}else{toast('Already reconciled','a-b');}}

// CSV Template & Import for Monthly
export function exportMoCsvTemplate(){
const rows=[['Ticker','Qty','BuyPrice']];
S.holdings.forEach(h=>rows.push([h.ticker,'','']));
const csv=rows.map(r=>r.join(',')).join('\n');
const blob=new Blob([csv],{type:'text/csv'});const a=document.createElement('a');
a.href=URL.createObjectURL(blob);a.download='zagx-month-template.csv';a.click();}

export function importMoCsv(e){
const file=e.target.files[0];if(!file)return;
const reader=new FileReader();reader.onload=ev=>{
const lines=ev.target.result.trim().split('\n');
let matched=0;
lines.forEach((line,li)=>{
if(li===0&&line.toLowerCase().includes('ticker'))return;// skip header
const parts=line.split(',').map(s=>s.trim());
if(parts.length<3)return;
const ticker=parts[0].toUpperCase(),qty=parseFloat(parts[1]),price=parseFloat(parts[2]);
if(!ticker||!qty||!price)return;
const idx=S.holdings.findIndex(h=>h.ticker===ticker);
if(idx<0)return;
// Fill the form inputs
const qEl=document.getElementById('mq_'+idx);const pEl=document.getElementById('mc_'+idx);
if(qEl)qEl.value=qty;if(pEl)pEl.value=price;
// Auto-stage
saveRow(idx);matched++;});
toast(matched+' entries imported from CSV','a-b');};
reader.readAsText(file);e.target.value='';}
